'use client'

import type { WhoAmIClient } from '@tebuto/api/clients/models/WhoAmIClient'
import type { BookEventFormValues } from '@tebuto/widget/booking/BookingEvent'
import { resolveCheckoutCustomerFields } from '@tebuto/widget/booking/functions'
import type { OnlinePaymentMethods, ParsedBookableEvent } from '@tebuto/widget/booking/types'
import { useCallback, useEffect, useRef, useState } from 'react'

const CHECKOUT_SESSION_INIT_ERROR = 'Fehler beim Erstellen der Zahlung. Bitte versuchen Sie es erneut.'

type CachedCheckoutSession = {
    initKey: string
    clientSecret: string
    checkoutSessionId: string
    returnUrl?: string
}

const checkoutSessionCache = new Map<string, CachedCheckoutSession>()

export type WidgetStripeCheckoutSessionState = {
    clientSecret: string | null
    checkoutSessionId: string | null
    returnUrl: string | undefined
    isInitializing: boolean
    initError: string | null
    reloadCheckoutSession: () => void
}

type CreateCheckoutSessionFn = (options: {
    event: ParsedBookableEvent
    firstName: string
    lastName: string
    email: string
    address?: {
        streetAndNumber: string
        city: { zip: string; name: string }
    }
    onlinePaymentMethod?: OnlinePaymentMethods[number]
}) => Promise<{ id: string; clientSecret: string; returnUrl?: string }>

function buildCheckoutSlotKey(event: ParsedBookableEvent): string {
    return `${event.ruleId}|${event.start}|${event.categoryId}`
}

function buildClientIdentityKey(
    bookingData: BookEventFormValues | null | undefined,
    clientWhoAmI: WhoAmIClient | null | undefined
): string {
    return [
        bookingData?.email ?? '',
        bookingData?.firstName ?? '',
        bookingData?.lastName ?? '',
        clientWhoAmI?.email ?? '',
        clientWhoAmI?.firstName ?? '',
        clientWhoAmI?.lastName ?? ''
    ].join('|')
}

export function useWidgetStripeCheckoutSession({
    enabled,
    bookingEvent,
    bookingData,
    clientWhoAmI,
    selectedOnlineMethod,
    createCheckoutSession
}: {
    enabled: boolean
    bookingEvent: ParsedBookableEvent | undefined
    bookingData: BookEventFormValues | null | undefined
    clientWhoAmI: WhoAmIClient | null | undefined
    selectedOnlineMethod?: OnlinePaymentMethods[number]
    createCheckoutSession: CreateCheckoutSessionFn
}): WidgetStripeCheckoutSessionState {
    const createCheckoutSessionRef = useRef(createCheckoutSession)
    createCheckoutSessionRef.current = createCheckoutSession

    const checkoutSlotKey = bookingEvent ? buildCheckoutSlotKey(bookingEvent) : null
    const clientIdentityKey = buildClientIdentityKey(bookingData, clientWhoAmI)

    const [clientSecret, setClientSecret] = useState<string | null>(null)
    const [checkoutSessionId, setCheckoutSessionId] = useState<string | null>(null)
    const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined)
    const [initError, setInitError] = useState<string | null>(null)
    const [isInitializing, setIsInitializing] = useState(false)
    const [reloadToken, setReloadToken] = useState(0)

    const reloadCheckoutSession = useCallback(() => {
        if (!checkoutSlotKey) {
            return
        }

        const initKey = `${checkoutSlotKey}|${clientIdentityKey}|${selectedOnlineMethod ?? ''}`
        checkoutSessionCache.delete(initKey)
        setClientSecret(null)
        setCheckoutSessionId(null)
        setReturnUrl(undefined)
        setInitError(null)
        setIsInitializing(true)
        setReloadToken((current) => current + 1)
    }, [checkoutSlotKey, clientIdentityKey, selectedOnlineMethod])

    useEffect(() => {
        if (!enabled || !bookingEvent || !bookingData || !checkoutSlotKey) {
            setIsInitializing(false)
            return
        }

        const event = bookingEvent
        const customer = resolveCheckoutCustomerFields(bookingData, clientWhoAmI)
        const initKey = `${checkoutSlotKey}|${clientIdentityKey}|${selectedOnlineMethod ?? ''}`

        const cachedSession = checkoutSessionCache.get(initKey)
        if (cachedSession) {
            setClientSecret(cachedSession.clientSecret)
            setCheckoutSessionId(cachedSession.checkoutSessionId)
            setReturnUrl(cachedSession.returnUrl)
            setInitError(null)
            setIsInitializing(false)
            return
        }

        let cancelled = false
        setIsInitializing(true)
        setInitError(null)
        setClientSecret(null)
        setCheckoutSessionId(null)
        setReturnUrl(undefined)

        if (!customer.email) {
            setInitError(CHECKOUT_SESSION_INIT_ERROR)
            setIsInitializing(false)
            return
        }

        async function initializeCheckoutSession(): Promise<void> {
            try {
                const response = await createCheckoutSessionRef.current({
                    event,
                    onlinePaymentMethod: selectedOnlineMethod,
                    ...customer
                })

                if (!response.clientSecret || !response.id) {
                    throw new Error('Checkout session response is missing clientSecret or id')
                }

                if (cancelled) {
                    return
                }

                checkoutSessionCache.set(initKey, {
                    initKey,
                    clientSecret: response.clientSecret,
                    checkoutSessionId: response.id,
                    returnUrl: response.returnUrl
                })
                setClientSecret(response.clientSecret)
                setCheckoutSessionId(response.id)
                setReturnUrl(response.returnUrl)
                setInitError(null)
            } catch {
                if (!cancelled) {
                    checkoutSessionCache.delete(initKey)
                    setInitError(CHECKOUT_SESSION_INIT_ERROR)
                }
            } finally {
                if (!cancelled) {
                    setIsInitializing(false)
                }
            }
        }

        void initializeCheckoutSession()

        return () => {
            cancelled = true
        }
    }, [
        enabled,
        bookingEvent,
        bookingData,
        checkoutSlotKey,
        clientIdentityKey,
        clientWhoAmI,
        selectedOnlineMethod,
        reloadToken
    ])

    return {
        clientSecret,
        checkoutSessionId,
        returnUrl,
        isInitializing,
        initError,
        reloadCheckoutSession
    }
}
